'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import Image from 'next/image';
import { RefreshCw, CheckCircle2 } from 'lucide-react';

interface SliderCaptchaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  image: string;
  onVerify: (position: number) => Promise<boolean>;
  onRefresh?: () => void;
}

export function SliderCaptchaDialog({
  open,
  onOpenChange,
  image,
  onVerify,
  onRefresh,
}: SliderCaptchaDialogProps) {
  const [sliderPosition, setSliderPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState<string>('');
  const sliderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const updateSliderPosition = useCallback((e: MouseEvent | React.MouseEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const maxPosition = rect.width - 40; // 滑块宽度
    const position = Math.max(0, Math.min(maxPosition, x));
    setSliderPosition(position);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || isVerified || isVerifying) return;
    updateSliderPosition(e);
  }, [isDragging, isVerified, isVerifying, updateSliderPosition]);

  const handleMouseUp = useCallback(async () => {
    if (!isDragging || isVerified || isVerifying) return;
    setIsDragging(false);
    
    // 验证滑块位置
    if (sliderPosition > 0) {
      setIsVerifying(true);
      setError('');
      
      try {
        const success = await onVerify(sliderPosition);
        if (success) {
          setIsVerified(true);
          // 立即关闭对话框，不延迟
          onOpenChange(false);
        } else {
          setError('验证失败，请重试');
          setSliderPosition(0);
        }
      } catch {
        setError('验证失败，请重试');
        setSliderPosition(0);
      } finally {
        setIsVerifying(false);
      }
    }
  }, [isDragging, isVerified, isVerifying, sliderPosition, onVerify, onOpenChange]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleReset = () => {
    setSliderPosition(0);
    setIsVerified(false);
    setIsVerifying(false);
    setError('');
    onRefresh?.();
  };

  useEffect(() => {
    if (open) {
      setSliderPosition(0);
      setIsVerified(false);
      setIsVerifying(false);
      setError('');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>滑块验证</DialogTitle>
          <DialogDescription>请拖动滑块完成验证</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="relative">
            <div
              ref={containerRef}
              className="relative w-full h-40 border-2 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 cursor-move"
              style={{ userSelect: 'none' }}
            >
              {image && (
                <Image
                  src={image}
                  alt="验证码背景"
                  width={500}
                  height={160}
                  className="w-full h-full object-cover"
                  draggable={false}
                  style={{ pointerEvents: 'none' }}
                  unoptimized
                />
              )}
              {/* 滑块 */}
              <div
                ref={sliderRef}
                className={`absolute top-0 h-full w-12 bg-blue-500/80 border-2 border-blue-600 cursor-move transition-all ${
                  isDragging ? 'shadow-lg' : ''
                } ${isVerified ? 'bg-green-500/80 border-green-600' : ''}`}
                style={{
                  left: `${sliderPosition}px`,
                  transition: isDragging ? 'none' : 'left 0.2s ease',
                }}
                onMouseDown={(e) => {
                  if (!isVerified && !isVerifying) {
                    e.preventDefault();
                    setIsDragging(true);
                  }
                }}
              >
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center">
                    {isVerified ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <svg
                        className="w-5 h-5 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    )}
                  </div>
                </div>
              </div>
              {/* 点击区域也可以拖动 - 但需要排除滑块区域 */}
              <div
                className="absolute inset-0 cursor-move"
                style={{ pointerEvents: 'auto' }}
                onMouseDown={(e) => {
                  // 如果点击的是滑块本身，不处理（由滑块的onMouseDown处理）
                  if (sliderRef.current && sliderRef.current.contains(e.target as Node)) {
                    return;
                  }
                  if (!isVerified && !isVerifying) {
                    e.preventDefault();
                    updateSliderPosition(e);
                    setIsDragging(true);
                  }
                }}
              />
            </div>
            <div className="mt-2 text-center text-xs text-gray-500 dark:text-gray-400">
              拖动滑块完成验证
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={isVerifying || isVerified}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              刷新
            </Button>

            <div className="text-sm text-gray-600 dark:text-gray-400">
              {isVerified ? '✓ 验证成功' : isVerifying ? '验证中...' : '请拖动滑块'}
            </div>

            <Button
              type="button"
              onClick={() => {
                if (sliderPosition > 0 && !isVerified && !isVerifying) {
                  handleMouseUp();
                }
              }}
              disabled={sliderPosition === 0 || isVerifying || isVerified}
            >
              {isVerifying ? '验证中...' : isVerified ? '验证成功' : '验证'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
